import { Grid, Pagination as MuiPagination, styled, useMediaQuery, useTheme } from '@mui/material';
import { Fragment, useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import PaginationStore from '../../UserData/_stores/PaginationStore';
import { EditPaginationLoader, FetchPaginationItems } from '../../UserData/_actions/UserDataActions';
import { UserImageStore } from '../../UserData/_stores/UserImageStore';
import { UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore';
import { UserAddressStore } from '../../UserData/_stores/UserAddressStore';
import { v4 as uuidv4 } from 'uuid';
import { UserCreditCardStore } from '../../UserData/_stores/UserCreditCardStore';
import { useAppDispatch } from '../_hooks/useAppDispatch';
import UserCartStore from '../../UserData/_stores/UserCartStore';
import { PhotoLabCreationStore } from '../../UserData/_stores/PhotoLabCreationStore';
import { useAppSelector } from '../_hooks/useAppSelector';
import { StoreProductStore } from '../../UserData/_stores/Store/StoreProductStore';
import StoreProductListStore from '../../UserData/_stores/Store/StoreProductListStore';
import PaginationItemListStore from '../../UserData/_stores/PaginationItemListStore';

const DEFAULT_LIMIT = 20

const StyledPagination = styled(MuiPagination)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'column',
	padding: theme.spacing(2, 0),

	[theme.breakpoints.down('md')]: {
		padding: theme.spacing(1, 0),
	}
}));

export interface ItemListSearchParams {
	[key: string]: string[]
}

export type ItemListFetchOptions = {
	silent?: boolean
}

export type Props = {
	listId?: string
	controlled?: boolean
	render: (items: JSX.Element | null, links: JSX.Element | null, loading: boolean, page: number) => JSX.Element
	loading?: boolean
	maxItemsCount?: number
	searchQuery?: string | ItemListSearchParams
	defaultSearchQuery?: string | ItemListSearchParams
	searchFields?: string[]
	page?: number
	limit?: number
	ids?: Array<string>
	searchPlaceHolder?: string
	excludeId?: string
	fetchItems?: (listId: string, formData: FormData, options?: ItemListFetchOptions) => void
	onUpdate?: (itemList:PaginationItemListStore|undefined) => void
	enabled?: boolean
} & ({
	dataType: 'images'
	item: (item: UserImageStore, index: number) => JSX.Element
} | {
	dataType: 'productDesigns'
	item: (item: UserProductDesignStore, index: number) => JSX.Element
} | {
	dataType: 'addresses'
	item: (item: UserAddressStore, index: number) => JSX.Element
} | {
	dataType: 'creditCards'
	item: (item: UserCreditCardStore, index: number) => JSX.Element
} | {
	dataType: 'orders'
	item: (item: UserCartStore, index: number) => JSX.Element
} | {
	dataType: 'photoLabCreations'
	item: (item: PhotoLabCreationStore, index: number) => JSX.Element
} | {
	dataType: 'storeProducts'
	item: (item: StoreProductStore, index: number) => JSX.Element
	paginationStore: StoreProductListStore
})

const areStringArraysEqual = (a: string[], b: string[]) => {
	if (a.length !== b.length) return false

	return a.every((value, index) => value === b[index])
}

const areSearchQueriesEqual = (
	a: string | ItemListSearchParams,
	b: string | ItemListSearchParams
) => {
	if (typeof a === 'string' || typeof b === 'string') {
		return a === b
	}

	const keys = new Set([...Object.keys(a), ...Object.keys(b)])

	for (const key of keys) {
		if (!areStringArraysEqual(a[key] ?? [], b[key] ?? [])) {
			return false
		}
	}

	return true
}

export default function ItemList(props: Props) {
	const listId = useMemo(() => props.listId ? props.listId : uuidv4(), [props.listId]);
	const enabled = props.enabled ?? true;

	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams()
	const theme = useTheme()
	const breakpointSmDown = useMediaQuery(theme.breakpoints.down('sm'))
	const defaultLimit = useRef<number | null>(props.limit ?? null)
	const previousControlledSearchQuery = useRef<string | ItemListSearchParams>(props.searchQuery ?? '')
	const previousControlledLimit = useRef(props.limit)
	const previousControlledIds = useRef(props.ids ?? [])
	const hasSearchQueryProp = props.searchQuery !== undefined


	const itemList = useAppSelector(state => props.dataType === 'storeProducts'
		? props.paginationStore.get('itemLists').get(listId)
		: (state.get('userData').get(props.dataType) as PaginationStore).get('itemLists').get(listId));
	const data = useAppSelector(state => props.dataType === 'storeProducts'
		? props.paginationStore.get('data')
		: (state.get('userData').get(props.dataType) as PaginationStore).get('data'));

	const [searchQuery, setSearchQuery] = useState(props.searchQuery || '')
	const [page, setPage] = useState(1)
	const [limit, setLimit] = useState(props.limit || DEFAULT_LIMIT)
	const [ids, setIds] = useState(props.ids || [])

	useEffect(() => {
		if (!hasSearchQueryProp) return

		const nextSearchQuery = props.searchQuery || ''
		const controlledSearchChanged = props.controlled === true
			&& !areSearchQueriesEqual(previousControlledSearchQuery.current, nextSearchQuery)
		previousControlledSearchQuery.current = nextSearchQuery

		setSearchQuery(value => areSearchQueriesEqual(value, nextSearchQuery) ? value : nextSearchQuery);
		if (controlledSearchChanged) setPage(1)
	}, [hasSearchQueryProp, props.controlled, props.searchQuery]);

	useEffect(() => {
		const controlledLimitChanged = props.controlled === true
			&& previousControlledLimit.current !== props.limit
		previousControlledLimit.current = props.limit

		if (!props.limit) return;

		if (defaultLimit.current === null) {
			defaultLimit.current = props.limit
		}

		setLimit(props.limit);
		if (controlledLimitChanged) setPage(1)
	}, [props.controlled, props.limit]);

	useEffect(() => {
		const nextIds = props.ids || []
		const controlledIdsChanged = props.controlled === true
			&& !areStringArraysEqual(previousControlledIds.current, nextIds)
		previousControlledIds.current = nextIds

		setIds(value => areStringArraysEqual(value, nextIds) ? value : nextIds);
		if (controlledIdsChanged) setPage(1)
	}, [props.controlled, props.ids]);

	const fetchItems = useCallback((options: ItemListFetchOptions = {}) => {
		if (!enabled) return;

		let tmpLimit = props.excludeId && page === 1 ? limit - 1 : limit;
		let offset = props.excludeId ? (
			page > 1 ? (limit - 1) + limit * (page - 2) : 0
		) : limit * (page - 1);
		let formData = new FormData();
		formData.append("offset", String(offset));

		if (typeof searchQuery === 'object') {
			const searchKeys = props.searchFields ?? Object.keys(searchQuery)

			searchKeys.forEach(key => {
				formData.append(key, searchQuery[key]?.join(',') ?? '')
			})
		} else {
			formData.append("search", searchQuery);
		}

		formData.append("limit", String(tmpLimit));

		ids.forEach((id) => formData.append("ids[]", id));

		if (props.excludeId) {
			formData.append('excludeId', props.excludeId);
		}

		if (props.fetchItems) {
			props.fetchItems(listId, formData, options)
		} else {
			if (!options.silent) {
				dispatch(EditPaginationLoader(props.dataType, listId, true));
			}

			dispatch(FetchPaginationItems(listId, formData, props.dataType).set({
				onSuccess: () => {
					dispatch(EditPaginationLoader(props.dataType, listId, false));
				},
				onFailure: () => {
					dispatch(EditPaginationLoader(props.dataType, listId, false));
				}
			}));
		}
	}, [dispatch, enabled, props.dataType, props.excludeId, props.searchFields, page, limit, searchQuery, ids, listId, props.fetchItems]);

	useEffect(() => {
		if (props.controlled) return;

		let queryString = new URLSearchParams(location.search)

		if (!hasSearchQueryProp) {
			if (props.searchFields) {
				const newSearchQuery: Record<string, string[]> = {}
				props.searchFields.forEach(searchField => {
					const fallbackSearchQuery = typeof props.defaultSearchQuery === 'object'
						? props.defaultSearchQuery[searchField] ?? []
						: []

					newSearchQuery[searchField] = queryString.has(searchField)
						? queryString.get(searchField)?.split(',') ?? []
						: fallbackSearchQuery;
				})
				setSearchQuery(value => areSearchQueriesEqual(value, newSearchQuery) ? value : newSearchQuery)
			} else {
				const newSearchQuery = queryString.get('search') ?? ''

				setSearchQuery(value => areSearchQueriesEqual(value, newSearchQuery) ? value : newSearchQuery)
			}
		}
		setLimit(Number(queryString.get('limit')) || defaultLimit.current || DEFAULT_LIMIT)
		setPage(Number(queryString.get('page')) || 1)
		const ids = queryString.get('ids');
		const newIds = ids ? ids.split(',') : []

		setIds(value => areStringArraysEqual(value, newIds) ? value : newIds)
	}, [location.search, props.controlled, props.defaultSearchQuery, props.searchFields, hasSearchQueryProp]);

	useEffect(() => {
		//Reset to page 1 when excludeId changes
		if (props.excludeId && page !== 1) {
			setPage(1);
		}
	}, [props.excludeId]);

	/*useEffect(() => {
		if(props.controlled) {
			updateStateControlled(props.page || page)
		}
	}, [props.limit, props.ids, props.page])*/

	useEffect(() => {
		let hasChanged = false

		if (!props.controlled && props.searchQuery !== undefined) {
			if (typeof props.searchQuery === 'object') {
				const keys = props.searchFields ?? Object.keys(props.searchQuery)
				for (let key of keys) {
					const value = props.searchQuery[key]?.join(',') ?? '';
					const defaultValue = typeof props.defaultSearchQuery === 'object'
						? props.defaultSearchQuery[key]?.join(',') ?? ''
						: ''

					if (value === '' || value === defaultValue) {
						if (searchParams.has(key)) {
							searchParams.delete(key)
							hasChanged = true
						}
					} else if (value !== searchParams.get(key)) {
						searchParams.set(key, value)
						hasChanged = true
					}
				}
			} else {
				const value = props.searchQuery.replace('%', '')
				const defaultValue = typeof props.defaultSearchQuery === 'string'
					? props.defaultSearchQuery.replace('%', '')
					: ''

				if (value === '' || value === defaultValue) {
					if (searchParams.has('search')) {
						searchParams.delete('search')
						hasChanged = true
					}
				} else if (value !== searchParams.get('search')) {
					searchParams.set('search', value)
					hasChanged = true
				}
			}

			//Reset page and update searchParams if they changed
			if (hasChanged) {
				searchParams.delete('page')

				setSearchParams(searchParams, {
					replace: true,
				})
			}
		}
	}, [props.searchQuery]);

	useEffect(() => {
		if (!props.controlled && props.limit !== undefined) {
			const value = String(props.limit)
			const queryLimit = Number(searchParams.get('limit'))

			if (Number.isFinite(queryLimit) && queryLimit > 0 && queryLimit !== props.limit) {
				return
			}

			if (props.limit === (defaultLimit.current ?? DEFAULT_LIMIT)) {
				if (searchParams.has('limit')) {
					searchParams.delete('page')
					searchParams.delete('limit')
					setSearchParams(searchParams, {
						replace: true,
					})
				}
			} else if (value !== searchParams.get('limit')) {
				searchParams.delete('page')
				searchParams.set('limit', value)
				setSearchParams(searchParams, {
					replace: true,
				})
			}
		}
	}, [props.limit]);

	useEffect(() => {
		if (!enabled) return;

		fetchItems()
	}, [enabled, fetchItems])

	useEffect(() => {
		if (!enabled) return;

		if (itemList && !itemList.get('valid') && !itemList.get('loading')) {
			fetchItems({
				silent: (itemList?.get('items').count() ?? 0) > 0,
			})
		}
	}, [enabled, fetchItems, itemList])

	useEffect(() => {
		if (props.onUpdate) {
			props.onUpdate(itemList)
		}
	}, [itemList, props.onUpdate])

	const renderLinks = () => {
		if (!enabled) return null;

		let totalPages = itemList?.get('total') ? Math.ceil(itemList.get('total') / limit) : 1;

		if (totalPages <= 1 && page === 1) {
			return null;
		}

		return <StyledPagination
			count={totalPages}
			showFirstButton={breakpointSmDown ? false : totalPages > 5}
			showLastButton={breakpointSmDown ? false : totalPages > 5}
			siblingCount={breakpointSmDown ? 0 : 1}
			page={page}
			onChange={(e, value) => {
				if (props.controlled) {
					setPage(value);
				} else {
					searchParams.set('page', String(Math.ceil(((value - 1) * limit + 1) / +limit)))
					if (limit === (defaultLimit.current ?? DEFAULT_LIMIT)) {
						searchParams.delete('limit')
					} else {
						searchParams.set('limit', String(limit))
					}
					navigate(location.pathname + '?' + searchParams.toString())
				}
			}}
		/>
	}

	const renderItems = () => {
		if (!enabled) return null;
		if (!itemList?.get('items').count()) return null;

		let renderedItems = itemList.get('items')
			.valueSeq()
			.take(props.maxItemsCount ? props.maxItemsCount : limit)
			.map((id, key) => {
				const itemData = data.get(id);
				if (!itemData) return null;

				return <Fragment key={id}>{props.item(itemData, key)}</Fragment>;
			});

		return <>{renderedItems}</>
	}

	return props.render(renderItems(), renderLinks(), enabled && (itemList?.get('loading') || (!itemList?.get('valid') && !itemList?.get('items').count())), page)
}
